import React from 'react';
import {Draggable, Droppable} from '@hello-pangea/dnd';
import Button from '../../../../../incase-domain/components/button/Button';
import '../../../../../styles/components/genericTable.scss';

interface GridProps<T> {
    columnsNumber?: number;
    columnsGap?: string;
    rowsGap?: string;
    rows: T[];
    rowIdentifier: (row: T) => string | number;
    loading?: boolean;
    renderItem: (row: T, loading?: boolean, onSelectItem?: (row: T) => void, isSelected?: boolean) => JSX.Element;
    onSelectedItem?: (item: T) => void;
    selectedRows: {[key: string | number]: T};
    enableSelect?: boolean;
    listDroppableId?: string;
    draggableIdOf?: (row: T) => string;
}

function Grid<T extends Record<string, unknown> | null>(props: GridProps<T>): JSX.Element {
    const {
        columnsNumber = 3,
        columnsGap = '1rem',
        rowsGap = '1rem',
        rows,
        loading,
        rowIdentifier,
        renderItem,
        onSelectedItem,
        selectedRows,
        enableSelect,
        listDroppableId,
        draggableIdOf,
    } = props;

    const renderGridItem = (row: T): JSX.Element => (
        <div key={rowIdentifier(row)} className="c-genericGrid__wrapper">
            <div>{renderItem(row, loading, onSelectedItem, !!selectedRows[rowIdentifier(row)])}</div>
            {enableSelect && selectedRows[rowIdentifier(row)] && (
                <div className="c-genericGrid__checkbox">
                    <Button
                        variant="ghost"
                        size="xl"
                        padded={false}
                        dynamicSize
                        color="secondary"
                        leading={selectedRows[rowIdentifier(row)] ? 'solar:check-square-bold' : 'solar:stop-linear'}
                        onClick={(e) => {
                            e?.stopPropagation();
                            onSelectedItem?.(row);
                        }}
                    />
                </div>
            )}
        </div>
    );

    if (listDroppableId && draggableIdOf) {
        return (
            <Droppable droppableId={listDroppableId} isDropDisabled>
                {(provided) => (
                    <div
                        className="c-genericGrid"
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        style={{
                            gridTemplateColumns: `repeat(${columnsNumber},  minmax(0, 1fr))`,
                            columnGap: columnsGap,
                            rowGap: rowsGap,
                        }}
                    >
                        {rows.map((row, index) => (
                            <Draggable key={rowIdentifier(row)} draggableId={draggableIdOf(row)} index={index}>
                                {(draggableProvided, draggableSnapshot) => (
                                    <div
                                        ref={draggableProvided.innerRef}
                                        {...draggableProvided.draggableProps}
                                        {...draggableProvided.dragHandleProps}
                                        className={draggableSnapshot.isDragging ? 'is-dragging' : undefined}
                                    >
                                        {renderGridItem(row)}
                                    </div>
                                )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                    </div>
                )}
            </Droppable>
        );
    }

    return (
        <div
            className="c-genericGrid"
            style={{
                gridTemplateColumns: `repeat(${columnsNumber},  minmax(0, 1fr))`,
                columnGap: columnsGap,
                rowGap: rowsGap,
            }}
        >
            {rows.map((row) => renderGridItem(row))}
        </div>
    );
}

export default Grid;
